import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Flex,
  Input,
  Portal,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import ReactCountryFlag from "react-country-flag";
import { COUNTRIES, Country } from "./countries_data";

interface SelectCountryProps {
  onSelect: (country: Country) => void;
  label?: string;
  placeholder?: string;
  value?: string;
  size?: "sm" | "md" | "lg";
  borderRadius?: string;
  dropdownPosition?: "top" | "bottom";
  /** Gap en px entre el trigger y el dropdown (default: 6) */
  dropdownOffset?: number;
  /** Ancho del componente (default: 100%) */
  w?: string | number;
  /** Mostrar solo la bandera cuando está seleccionado (default: false) */
  flagOnly?: boolean;
  /** Ancho del trigger/input (default: hereda de w) */
  triggerWidth?: string | number;
  /** Ancho del dropdown (default: hereda de w) */
  dropdownWidth?: string | number;
}

const sizeMap = {
  sm: { px: "10px", fontSize: "sm" as const, height: "32px" },
  md: { px: "12px", fontSize: "sm" as const, height: "38px" },
  lg: { px: "14px", fontSize: "md" as const, height: "46px" },
};

const SelectCountry: React.FC<SelectCountryProps> = ({
  onSelect,
  label,
  placeholder = "Seleccionar país",
  value,
  size = "md",
  borderRadius = "7px",
  dropdownPosition = "bottom",
  dropdownOffset = 6,
  w = "100%",
  flagOnly = false,
  triggerWidth,
  dropdownWidth,
}) => {
  const [isOpen, setIsOpen]                   = useState(false);
  const [searchTerm, setSearchTerm]           = useState("");
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [rect, setRect]                       = useState<DOMRect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef     = useRef<HTMLInputElement>(null);
  const dropdownRef  = useRef<HTMLDivElement>(null);

  const bg          = useColorModeValue("white",    "gray.800");
  const border      = useColorModeValue("gray.200", "gray.600");
  const borderFocus = useColorModeValue("blue.400", "blue.300");
  const text        = useColorModeValue("gray.700", "gray.200");
  const labelColor  = useColorModeValue("gray.700", "gray.300");
  const hoverBg     = useColorModeValue("gray.50",  "gray.700");
  const selectedBg  = useColorModeValue("blue.50",  "blue.900");
  const selectedTxt = useColorModeValue("blue.600", "blue.200");
  const mutedText   = useColorModeValue("gray.400", "gray.500");
  const codeColor   = useColorModeValue("blue.500", "blue.300");
  const scrollThumb = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");
  const rowDivider  = useColorModeValue("gray.100", "gray.700");
  const searchBorder = useColorModeValue("gray.100", "gray.700");
  const s = sizeMap[size];

  useEffect(() => {
    if (value) {
      const c = COUNTRIES.find((c) => c.iso === value || c.code === value);
      if (c) setSelectedCountry(c);
    }
  }, [value]);

  // Close on click outside — check both trigger container AND portal dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      const insideTrigger  = containerRef.current?.contains(t);
      const insideDropdown = dropdownRef.current?.contains(t);
      if (!insideTrigger && !insideDropdown) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close on scroll — only when scroll originates outside the dropdown portal
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setIsOpen(false);
      setSearchTerm("");
    };
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [isOpen]);

  // Focus search input in portal when opening
  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 0);
  }, [isOpen]);

  const open = () => {
    if (containerRef.current) setRect(containerRef.current.getBoundingClientRect());
    setIsOpen(true);
  };
  const close = () => { setIsOpen(false); setSearchTerm(""); };

  const handleSelect = (country: Country) => {
    setSelectedCountry(country);
    onSelect(country);
    close();
  };

  const filtered = COUNTRIES.filter(({ country, code, iso }) => {
    const q = searchTerm.toLowerCase();
    return country.toLowerCase().includes(q) || code.includes(q) || iso.toLowerCase().includes(q);
  });

  return (
    <Box ref={containerRef} position="relative" w={w}>
      {label && (
        <Text fontSize="sm" fontWeight="600" color={labelColor} mb="6px">
          {label}
        </Text>
      )}

      {/* ── Trigger — always static display, never morphs into search ── */}
      <Flex
        align="center"
        w={triggerWidth || w}
        minW={0}
        overflow="hidden"
        bg={bg}
        border="1px solid"
        borderColor={isOpen ? borderFocus : border}
        borderRadius={borderRadius}
        px={s.px}
        h={s.height}
        transition="border-color 0.2s"
        cursor="pointer"
        onClick={() => isOpen ? close() : open()}
        _hover={{ borderColor: borderFocus }}
      >
        {selectedCountry ? (
          flagOnly ? (
            <Box boxShadow="0 2px 6px rgba(0,0,0,0.12)" borderRadius="2px" flexShrink={0}>
              <ReactCountryFlag countryCode={selectedCountry.iso} svg style={{ width: "20px", height: "20px", borderRadius: "2px", display: "block" }} />
            </Box>
          ) : (
            <Flex align="center" gap="6px" flex={1} minW={0} overflow="hidden">
              <Box boxShadow="0 2px 6px rgba(0,0,0,0.12)" borderRadius="2px" flexShrink={0}>
                <ReactCountryFlag countryCode={selectedCountry.iso} svg style={{ width: "18px", height: "18px", borderRadius: "2px", display: "block" }} />
              </Box>
              <Text fontSize={s.fontSize} fontWeight="500" color={text} isTruncated flex={1} minW={0}>
                {selectedCountry.country}
              </Text>
            </Flex>
          )
        ) : (
          <Text fontSize={s.fontSize} color={mutedText} flex={1} minW={0} isTruncated>{placeholder}</Text>
        )}

        <ChevronDownIcon
          color={mutedText}
          boxSize="16px"
          ml="8px"
          flexShrink={0}
          transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
          transition="transform 0.2s"
        />
      </Flex>

      {/* ── Dropdown — portaled to body; search input lives here (position:fixed = no Drawer scroll) ── */}
      {isOpen && rect && (
        <Portal>
          <Box
            ref={dropdownRef}
            position="fixed"
            w={dropdownWidth ?? `${rect.width}px`}
            top={dropdownPosition === "bottom"
              ? `${rect.bottom + dropdownOffset}px`
              : `${rect.top - dropdownOffset - 300}px`}
            left={`${rect.left}px`}
            bg={bg}
            border="1px solid"
            borderColor={border}
            borderRadius={borderRadius}
            boxShadow="0 8px 24px rgba(0,0,0,0.10)"
            zIndex={9999}
            overflow="hidden"
          >
            {/* Search input — fixed inside portal, no scroll triggered on Drawer */}
            <Box
              px={s.px}
              py="8px"
              borderBottom="1px solid"
              borderColor={searchBorder}
            >
              <Input
                ref={inputRef}
                variant="unstyled"
                fontSize={s.fontSize}
                placeholder="Buscar país..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                color={text}
                _placeholder={{ color: mutedText }}
              />
            </Box>

            {/* Scrollable country list */}
            <Box
              maxH="220px"
              overflowY="auto"
              css={{
                "&::-webkit-scrollbar": { width: "5px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": { background: scrollThumb, borderRadius: "3px" },
              }}
            >
              {filtered.length > 0 ? (
                filtered.map((country, i) => {
                  const active = selectedCountry?.iso === country.iso;
                  return (
                    <Flex
                      key={`${country.iso}-${i}`}
                      align="center"
                      px={s.px}
                      py="8px"
                      cursor="pointer"
                      bg={active ? selectedBg : "transparent"}
                      _hover={{ bg: active ? selectedBg : hoverBg }}
                      transition="background 0.12s"
                      onClick={() => handleSelect(country)}
                      borderBottom="1px solid"
                      borderColor={i < filtered.length - 1 ? rowDivider : "transparent"}
                    >
                      <Box flexShrink={0} mr="10px" boxShadow="0 2px 6px rgba(0,0,0,0.12)" borderRadius="2px">
                        <ReactCountryFlag countryCode={country.iso} svg style={{ width: "20px", height: "20px", borderRadius: "2px", display: "block" }} />
                      </Box>
                      <Box flex={1} minW={0}>
                        <Text
                          fontSize={s.fontSize}
                          fontWeight={active ? "600" : "400"}
                          color={active ? selectedTxt : text}
                          noOfLines={1}
                        >
                          {country.country}
                        </Text>
                        <Text fontSize="11px" color={mutedText}>{country.iso}</Text>
                      </Box>
                      <Text fontSize="xs" fontWeight="600" color={codeColor} flexShrink={0} ml="8px">
                        ({country.code})
                      </Text>
                    </Flex>
                  );
                })
              ) : (
                <Flex align="center" justify="center" py="20px">
                  <Text color={mutedText} fontSize="sm">Sin resultados</Text>
                </Flex>
              )}
            </Box>
          </Box>
        </Portal>
      )}
    </Box>
  );
};

export default SelectCountry;
